import React, { useEffect, useState } from 'react';
import { Table, Button, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import PaginationControls from '../components/PaginationControls';
import { getUser } from '../utils/auth';

const PAGE_SIZE = 20;
const DELETE_ALLOWED_ROLES = ['admin', 'masteradmin'];

function DepartmentList() {
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]); // For the company filter dropdown, independent of the current page
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const navigate = useNavigate();
  const canDelete = DELETE_ALLOWED_ROLES.includes(getUser()?.role);
  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [loading, setLoading] = useState(true); // Loading state
  const [sortConfig, setSortConfig] = useState({ key: 'company', direction: 'asc' }); // Sorting state
  const [filteredCompany, setFilteredCompany] = useState(''); // Filtering by company

  // Fetch departments from the backend (server-side paginated, sorted, filtered)
  useEffect(() => {
    const params = new URLSearchParams({
      page,
      sort: sortConfig.key,
      direction: sortConfig.direction,
    });
    if (filteredCompany) {
      params.set('company', filteredCompany);
    }
    fetch(`${API_BASE_URL}/departments/get_departments.php?${params.toString()}`,{
      credentials: 'include'  // This ensures cookies (including session cookies) are sent
    })
      .then(response => response.json())
      .then(data => {
        setDepartments(data.items || []);
        setTotalPages(data.total_pages || 1);
        setTotalRows(data.total_rows || 0);
        setLoading(false);
      })
      .catch(error => console.error('Error fetching companies:', error));
  }, [page, sortConfig, filteredCompany]);

  // Company list for the filter dropdown — fetched independently of the
  // current page so it always shows every company, not just ones visible
  // on the current page of departments.
  useEffect(() => {
    fetch(`${API_BASE_URL}/companies/get_companies.php`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCompanies(data);
        }
      })
      .catch(error => console.error('Error fetching companies:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (id) => {
    navigate(`/config/company-department/${id}`);
  };



  const handleDelete = async (id) => {
    const result = await Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this department?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    fetch(`${API_BASE_URL}/departments/delete_department.php?id=${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setDepartments(departments.filter(department => department.id !== id));
        } else {
          Swal.fire({ icon: 'warning', text: data.message || 'Failed to delete department' });
        }
      })
      .catch(error => console.error('Error deleting department:', error));
  };

    // Sorting function
    const sortDepartments = (key) => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
      setPage(1);
    };

    // Filtering function
    const handleCompanyFilter = (e) => {
      setFilteredCompany(e.target.value);
      setPage(1);
    };

  return (
    <div>
      <h2>List of Companies & Departments</h2>
      {/* Company filter */}
      <Form.Group controlId="filterByCompany" className="row align-items-center">
        <Form.Label className="col-sm-3">Filter by Company</Form.Label>
        <div className="col-sm-9">
        <Form.Control as="select" value={filteredCompany} onChange={handleCompanyFilter}>
          <option value="">All Companies</option>
          {companies.map((company) => (
            <option key={company.id} value={company.name}>
              {company.name}
            </option>
          ))}
        </Form.Control>
        </div>
      </Form.Group>
      <Table striped bordered hover responsive className='mt-2'>
        <thead>
          <tr>
          <th>#</th>
            <th onClick={() => sortDepartments('company')}>Company Name</th>
            <th onClick={() => sortDepartments('name')}>Department</th>
            <th>Contact Information</th>
          </tr>
        </thead>
        <tbody>
          {departments.length > 0 ? (
            departments.map((department,index) => (
              <tr key={department.id}>
                <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                <td>{department.company}</td>
                <td>{department.name}</td>
                <td>{department.contact_info}</td>
                <td>
                  <Button 
                    variant="warning" 
                    className="me-2" 
                    onClick={() => handleEdit(department.id)}
                  >
                    Edit
                  </Button>
                  {canDelete && (
                  <Button
                    variant="danger"
                    onClick={() => handleDelete(department.id)}
                  >
                    Delete
                  </Button>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="text-center">
                No company available.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <PaginationControls page={page} totalPages={totalPages} totalRows={totalRows} onPageChange={setPage} />
      <Link to="/config/company-department">
          <Button variant="primary" className="m-1">Add New Department</Button>
        </Link>
        
    </div>
          
  );
}

export default DepartmentList;
